import { useEffect, useMemo, useState } from 'react';
import { Option, UseSelectProps } from '@/components/Select/types';
import { KeyValue } from '@/types';
import { getCurrentIndex, getMappedItem, makeDefaultFilter } from './functions';
import { debounce, isEqual, isObject } from '@/utils';
import { useDebounce } from '@/hooks';
import { SelectPosition } from './types';
import { getNoOnlyWhiteSpaces } from '@/utils/string';

export const useSelect = ({ ...props }: UseSelectProps) => {
  const {
    value,
    onUpdate,
    inputRef,
    optionsContentRef,
    disabled = false,
    options = [],
    optionLabel = 'value',
    optionValue = 'id',
    emitValue = false,
    filterOnFocus = false,
    lazy = false,
    debounceFilter = 0,
    filter = makeDefaultFilter(optionLabel),
  } = props;

  const [localOptions, setLocalOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [buffer, setBuffer] = useState('');
  const [bufferChanged, setBufferChanged] = useState(false);
  const [activeItem, setActiveItem] = useState<number>(null);
  const [currentValue, setCurrentValue] = useState<Option>('');
  const [position, setPosition] = useState<SelectPosition>({ top: 0, left: 0, width: 0, bottom: 0, showOnTop: false });
  const [positionReference, setPositionReference] = useState([0, 0, 0]);
  const [filterIsProcessing, setFilterIsProcessing] = useState(false);
  const debounceBuffer = useDebounce(buffer, debounceFilter);

  const getLabel = (val: Option) => (getMappedItem(val, optionLabel) ?? '').toString();
  const getValue = (val: Option = '') => getMappedItem(val, optionValue);
  const isActive = (index: number) => index === activeItem;
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const displayValue = useMemo(
    () => getNoOnlyWhiteSpaces(bufferChanged ? buffer : getLabel(currentValue)),
    [buffer, currentValue, bufferChanged],
  );

  const inputFocus = () => {
    const inputElement: HTMLInputElement = inputRef.current.shadowRoot.querySelector('input');
    const localLabel = getLabel(currentValue);
    const len = Math.max(buffer.length, localLabel.length);
    inputElement.setSelectionRange(len, len);
    inputElement.focus();

    if (filterOnFocus && !buffer.length) setBuffer(localLabel);
  };

  const calculateShowPosition = () => {
    if (!isOpen) return;

    const contentHeight = optionsContentRef.current.offsetHeight;
    const topOffset = inputRef.current.getBoundingClientRect().top;
    const inputHeight = inputRef.current.offsetHeight;
    const totalHeight = window.innerHeight;
    const endOfInput = topOffset + inputHeight;

    const showOnTop = totalHeight - (contentHeight + 24) < endOfInput;
    const { width, left } = inputRef.current.getBoundingClientRect();
    const top = Math.ceil(endOfInput + 8);
    const bottom = Math.ceil(window.innerHeight - topOffset);

    setPosition({ showOnTop, width, top, bottom, left });
  };

  const toggleIsOpen = () => {
    if (disabled) return;

    setIsOpen(!isOpen);
  };

  const selectItem = (index: number) => {
    if (index >= localOptions.length) return;

    setCurrentValue(localOptions[index]);
    setActiveItem(index);
    close();
  };

  const onChangeCurrentItem = () => {
    const localValue = getValue(currentValue);

    if (isEqual(getValue(value), localValue) || localValue === '') return;

    onUpdate(emitValue ? localValue : currentValue);
  };

  const changeBuffer = (localValue: string) => {
    setBuffer(localValue);
    if (!bufferChanged) setBufferChanged(true);
  };

  const onLocalType = (event: CustomEvent) => {
    const localValue: string = (event.detail.value ?? '').toString();
    const val = getNoOnlyWhiteSpaces(localValue);
    changeBuffer(val);
    inputRef.current.value = val;
  };

  const navigate = (direction: 'up' | 'down') => {
    if (!isOpen) {
      open();
      return;
    }

    if (direction === 'up' && activeItem > 0) {
      setActiveItem((current) => current - 1);
    }

    if (direction === 'down' && activeItem < localOptions.length - 1) {
      setActiveItem((current) => current + 1);
    }
  };

  const onDeleteOrBackspace = (event: React.KeyboardEvent<HTMLFuelTextFieldElement>) => {
    event.preventDefault();
    open();
    setBuffer('');
    setBufferChanged(true);
    inputFocus();
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLFuelTextFieldElement> = (event) => {
    const { key } = event;

    if (['ArrowUp', 'ArrowDown', 'Space'].includes(key)) event.preventDefault();

    const commonActions = { ArrowUp: () => navigate('up'), ArrowDown: () => navigate('down'), Escape: close };
    const openedActions = { Enter: () => selectItem(activeItem), Tab: () => selectItem(activeItem) };
    const closedActions = {
      Delete: () => onDeleteOrBackspace(event),
      Backspace: () => onDeleteOrBackspace(event),
      Enter: open,
    };

    const actions: KeyValue = { ...commonActions, ...(isOpen ? openedActions : closedActions) };

    if (actions[key]) {
      actions[key]();
      return;
    }

    if (/^\w$/i.test(key) && !isOpen) {
      changeBuffer(key);
      open();
    }
  };

  const updateOptions = (result: Option[]) => {
    if (!Array.isArray(result) || isEqual(result, localOptions)) {
      setFilterIsProcessing(false);
      return;
    }

    const currentIndex = getCurrentIndex(localOptions, value, optionValue);

    setLocalOptions(result);
    setActiveItem(currentIndex >= 0 ? currentIndex : 0);
    setFilterIsProcessing(false);
  };

  const onUpdateBuffer = () => {
    setFilterIsProcessing(true);

    if (Boolean(debounceFilter)) {
      setLocalOptions([]);
      if (buffer !== debounceBuffer) return;
    }

    const filteredOptions = filter(buffer, options);

    if (filteredOptions instanceof Promise) {
      filteredOptions.then(updateOptions);
      return;
    }

    updateOptions(filteredOptions);
  };

  const onUpdateOptions = () => {
    updateOptions(options);
    if (lazy) return;
    const currentIndex = getCurrentIndex(options, value, optionValue);
    if (currentIndex === -1) return;
    const localValue = options[currentIndex];
    if (options === undefined) return;
    setCurrentValue(localValue);
    setActiveItem(currentIndex);
  };

  const scrollToItem = () => {
    const list = Array.from(optionsContentRef?.current?.children ?? []) as HTMLElement[];
    const item = list[activeItem];
    if (!item) return;

    optionsContentRef.current.scrollTop = item.offsetTop - item.offsetLeft;
  };

  const onUmnounted = () => {
    setIsOpen(false);
    setActiveItem(null);
  };

  const onChangeValue = () => {
    if (value === undefined) {
      setCurrentValue(undefined);
      setActiveItem(null);
      return;
    }

    const currentIndex = getCurrentIndex(localOptions, value, optionValue);
    if (currentIndex === -1 && !lazy) return;

    if (lazy && value && currentIndex === -1) {
      const item = isObject(value) ? { ...(value as KeyValue) } : value;
      setLocalOptions([item]);
      setCurrentValue(item);
      return;
    }

    const localValue = localOptions[currentIndex];

    if (localValue === undefined || isEqual(localValue, currentValue)) return;

    setCurrentValue(localValue);
    setActiveItem(currentIndex);
  };

  const onChangeOpenStatus = () => {
    const isOpenTrue = () => {
      inputFocus();
      calculateShowPosition();
      scrollToItem();

      if (activeItem === null) {
        const currentIndex = getCurrentIndex(localOptions, value, optionValue);
        setActiveItem(currentIndex > 0 ? currentIndex : 0);
      }
    };

    const isOpenFalse = () => {
      setBuffer('');
      setBufferChanged(false);
    };

    isOpen ? isOpenTrue() : isOpenFalse();
  };

  useEffect(() => {
    const onScroll = () => setPositionReference(([, ...rest]) => [window.scrollY, ...rest]);
    const debouncedOnScroll = debounce(onScroll, 50);

    document.addEventListener('scroll', debouncedOnScroll, { passive: true });

    return () => {
      document.removeEventListener('scroll', debouncedOnScroll);
      onUmnounted();
    };
  }, []);

  useEffect(calculateShowPosition, [localOptions, positionReference]);
  useEffect(onChangeCurrentItem, [currentValue]);
  useEffect(onChangeOpenStatus, [isOpen]);
  useEffect(onChangeValue, [value]);
  useEffect(onUpdateBuffer, [buffer, debounceBuffer]);
  useEffect(onUpdateOptions, [options]);
  useEffect(scrollToItem, [activeItem]);

  return {
    buffer: debounceBuffer,
    activeItem,
    close,
    displayValue,
    filterIsProcessing,
    getLabel,
    isActive,
    isOpen,
    localOptions,
    onKeyDown,
    onLocalType,
    selectItem,
    position,
    toggleIsOpen,
  };
};
